import { useState, useEffect } from 'react';

interface User {
  id: string;
  company_id?: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔍 [AUTH DEBUG] useAuth effect running...');
    
    // Try to get user from window object first (if set by server-side)
    if (window.user) {
      console.log('✅ [AUTH DEBUG] Found user in window object:', window.user);
      setUser(window.user);
      setLoading(false);
      return;
    }

    // Try to get user from localStorage
    const storedUser = localStorage.getItem('user');
    const authToken = localStorage.getItem('auth_token');
    
    console.log('🔍 [AUTH DEBUG] Checking localStorage...');
    console.log('👤 Stored User Present:', !!storedUser);
    console.log('🔑 Auth Token Present:', !!authToken);
    console.log('🔑 Auth Token Preview:', authToken ? `${authToken.substring(0, 10)}...` : 'NONE');
    
    // No hardcoded test users - use real authentication only
    if (!storedUser && !authToken) {
      console.log('🔍 [AUTH DEBUG] No stored auth data found, attempting API fetch...');
      // Try to fetch user from API instead of creating mock data
      fetchCurrentUser();
      return;
    }
    
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log('✅ [AUTH DEBUG] Parsed user from localStorage:', parsedUser);
        setUser(parsedUser);
        setLoading(false);
        return;
      } catch (error) {
        console.error('❌ [AUTH DEBUG] Failed to parse stored user:', error);
      }
    }

    // Try to fetch user from API
    console.log('🌐 [AUTH DEBUG] Attempting to fetch user from API...');
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      console.log('🌐 [AUTH DEBUG] Fetching user from /api/auth/me...');
      
      const authToken = localStorage.getItem('auth_token');
      console.log('🔑 [AUTH DEBUG] Using auth_token:', authToken ? `${authToken.substring(0, 10)}...` : 'NONE');
      
      // Only make API call if we have a valid auth token
      if (!authToken) {
        console.warn('⚠️ [AUTH DEBUG] No auth token found, cannot fetch user');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      console.log('📥 [AUTH DEBUG] API Response Status:', response.status);
      console.log('📥 [AUTH DEBUG] API Response OK:', response.ok);
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('✅ [AUTH DEBUG] Successfully fetched response:', responseData);
        
        // Extract user data from the response format {success: true, data: user}
        const userData = responseData.data || responseData;
        console.log('✅ [AUTH DEBUG] Extracted user data:', userData);
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Also check if we need to set authToken
        if (userData.token) {
          console.log('🔑 [AUTH DEBUG] Setting auth_token from user data');
          localStorage.setItem('auth_token', userData.token);
        }
      } else {
        console.warn('⚠️ [AUTH DEBUG] API response not OK:', response.status, response.statusText);
        const errorText = await response.text();
        console.warn('⚠️ [AUTH DEBUG] Error response:', errorText);
      }
    } catch (error) {
      console.error('❌ [AUTH DEBUG] Failed to fetch current user:', error);
    } finally {
      console.log('🏁 [AUTH DEBUG] fetchCurrentUser completed, setting loading to false');
      setLoading(false);
    }
  };

  const getCurrentUser = (): User | null => {
    return user;
  };

  const isAuthenticated = (): boolean => {
    return user !== null;
  };

  const hasPermission = (permission: string): boolean => {
    return user?.permissions?.includes(permission) || false;
  };

  const isBroker = (): boolean => {
    return user?.role === 'broker' || user?.role === 'admin';
  };

  return {
    user,
    loading,
    getCurrentUser,
    isAuthenticated,
    hasPermission,
    isBroker,
    refresh: fetchCurrentUser
  };
};